import { FC } from 'react'
import { useTimeSeriesMeasurements, TimeSeriesAggregateOptions } from '../../timeSeries/useTimeSeriesMeasurements'
import { useTimeSeries } from '../../timeSeries/useTimeSeries'
import { SeriesFilter } from '../../timeSeries/timeSeriesFilters'
import { Typography, Skeleton } from '@mui/material'
import ChartContainer, { ChartContainerProps } from './ChartContainer'
import BarChart, { TimeSeriesBarChartOptions } from './charts/BarChart'
import PieChart, { TimeSeriesPieChartOptions } from './charts/PieChart'
import TableChart, { TimeSeriesTableChartOptions } from './charts/TableChart'
import NumberChart, { TimeSeriesNumberChartOptions } from './charts/NumberChart'
import LineChart, { TimeSeriesLineChartOptions } from './charts/LineChart'
import AreaChart, { TimeSeriesAreaChartOptions } from './charts/AreaChart'
import LineAreaRangeChart, { TimeSeriesLineAreaRangeChartOptions } from './customCharts/LineAreaRangeChart'
import { useTranslation } from 'react-i18next'
import { ErrorBoundary } from 'react-error-boundary'
import { CommonChartProps } from './commonChartOptions'
import ChartErrorFallback from './ErrorFallback'
import slugify from 'slugify'

type ChartType = 'area' | 'bar' | 'pie' | 'table' | 'number' | 'line' | 'lineAreaRange'

const charts = {
    area: AreaChart,
    bar: BarChart,
    pie: PieChart,
    table: TableChart,
    number: NumberChart,
    line: LineChart,
    lineAreaRange: LineAreaRangeChart,
}

interface ChartOptions {
    area: TimeSeriesAreaChartOptions
    bar: TimeSeriesBarChartOptions
    pie: TimeSeriesPieChartOptions
    table: TimeSeriesTableChartOptions
    number: TimeSeriesNumberChartOptions
    line: TimeSeriesLineChartOptions
    lineAreaRange: TimeSeriesLineAreaRangeChartOptions
}

interface TimeSeriesChartProps<T extends ChartType> extends CommonChartProps {
    title?: string
    filters: SeriesFilter
    type: T
    options: ChartOptions[T]
    aggregateOptions?: TimeSeriesAggregateOptions
    exporting?: boolean
}

const TimeSeriesChart = <T extends ChartType>({
    filters,
    type,
    options,
    aggregateOptions,
    labelGetter,
    ...otherChartProps
}: TimeSeriesChartProps<T>) => {
    const { isLoading, series } = useTimeSeriesMeasurements(filters, { labelGetter }, aggregateOptions)
    const { t } = useTranslation('site', { keyPrefix: 'dashboard' })

    const ChartComponent = charts[type] as FC<CommonChartProps & { options: ChartOptions[T]; series: typeof series }>

    const measurementsLoading = series.length && series.every((s) => s.isLoading)

    if (isLoading || measurementsLoading) {
        return <Skeleton variant="rectangular" sx={{ height: 300 }} />
    }

    if (!series.length) {
        return <Typography color="text.secondary">{t('fallBacks.noData')}</Typography>
    }

    return (
        <ChartComponent
            options={options}
            series={series}
            {...otherChartProps}
            slug={otherChartProps.title && slugify(otherChartProps.title, { lower: true })}
        />
    )
}

type TimeSeriesChartWrapperProps<T extends ChartType> = TimeSeriesChartProps<T> &
    ChartContainerProps & { translateUnit?: boolean }

const TimeSeriesChartWrapper = <T extends ChartType>({
    title,
    unit,
    helper,
    size = '1',
    exporting = true,
    ...props
}: TimeSeriesChartWrapperProps<T>) => {
    const { t } = useTranslation('site', { keyPrefix: 'dashboard.units' })
    const { getSeriesUnit } = useTimeSeries()
    const seriesUnit = unit === 'auto' ? getSeriesUnit(props.filters).unit : unit
    const translatedUnit = seriesUnit ? t(seriesUnit, { defaultValue: seriesUnit }) : seriesUnit

    return (
        <ChartContainer title={title} unit={translatedUnit} size={size} helper={helper}>
            <ErrorBoundary fallback={<ChartErrorFallback />}>
                <TimeSeriesChart exporting={exporting} {...props} title={title} />
            </ErrorBoundary>
        </ChartContainer>
    )
}

export default TimeSeriesChartWrapper
